import React, { createContext, useState, useEffect } from 'react';
import { AUTH_LOGOUT_EVENT, AUTH_TOKEN_REFRESHED_EVENT } from '../api/client';

// Creating the AuthContext that will be used to provide authentication data
export const AuthContext = createContext();

/**
 * getJwtExpiryMs - Reads a JWT exp claim and converts it to milliseconds.
 */
const getJwtExpiryMs = (jwtToken) => {
  if (!jwtToken) {
    return null;
  }

  try {
    // Decode the JWT payload without validating it so the frontend can schedule local logout.
    const base64Payload = jwtToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = base64Payload.padEnd(base64Payload.length + ((4 - (base64Payload.length % 4)) % 4), '=');
    const payload = JSON.parse(window.atob(paddedPayload));
    return payload.exp ? payload.exp * 1000 : null;
  } catch (err) {
    return null;
  }
};

/**
 * AuthProvider - A context provider component that handles authentication logic.
 * It provides authentication state (token, username) and login/logout functions to the app.
 * 
 * @component
 * @example
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * 
 * @param {Object} props - The props for the component.
 * @param {React.ReactNode} props.children - The child components that will have access to the AuthContext.
 */
export const AuthProvider = ({ children }) => {
  // State for storing the authentication token (access_token)
  const [token, setToken] = useState(localStorage.getItem('access_token') || null);
  // State for storing the username of the logged-in user
  const [username, setUsername] = useState(localStorage.getItem('username') || null);

  /**
   * login - Logs in the user by storing the authentication token and username in localStorage
   * and updating the context state.
   * 
   * @param {string} access_token - The authentication token.
   * @param {string} username - The username of the logged-in user.
   */
  const login = (access_token, username) => {
    // Storing token and username in localStorage
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('username', username);

    // Setting the context state with the token and username
    setToken(access_token);
    setUsername(username);
  };

  /**
   * logout - Logs out the user by clearing the token and username from localStorage
   * and updating the context state.
   */
  const logout = () => {
     // Clearing token and username from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token'); // Clear refresh token so it cannot be reused after logout
    localStorage.removeItem('username');

    // Clearing the context state
    setToken(null);
    setUsername(null);
  };

  /**
   * useEffect - A React hook that runs on component mount. It checks if there is an
   * existing token and username in localStorage and sets them in the state.
   */
  useEffect(() => {
    const savedToken = localStorage.getItem('access_token');
    const savedUsername = localStorage.getItem('username');

    // If a token exists in localStorage, set it in the state
    if (savedToken) {
      setToken(savedToken);
    }

    // If a username exists in localStorage, set it in the state
    if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []); // Empty dependency array ensures this runs only once when the component mounts

  /**
   * useEffect - Keeps AuthContext in sync when the shared API client refreshes or clears auth.
   */
  useEffect(() => {
    const handleTokenRefreshed = (event) => {
      // Update the in-memory access token after the API client refreshes it.
      setToken(event.detail?.access_token || localStorage.getItem('access_token'));
    };

    const handleAuthLogout = () => {
      // Clear context state after the API client removes expired or invalid tokens.
      setToken(null);
      setUsername(null);
    };

    window.addEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
    window.addEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);

    return () => {
      window.removeEventListener(AUTH_TOKEN_REFRESHED_EVENT, handleTokenRefreshed);
      window.removeEventListener(AUTH_LOGOUT_EVENT, handleAuthLogout);
    };
  }, []);

  /**
   * useEffect - Logs the user out when the refresh token reaches its expiry time.
   */
  useEffect(() => {
    const refreshToken = localStorage.getItem('refresh_token');
    const refreshExpiryMs = getJwtExpiryMs(refreshToken);

    if (!token || !refreshExpiryMs) {
      return undefined;
    }

    const msUntilExpiry = refreshExpiryMs - Date.now();

    if (msUntilExpiry <= 0) {
      logout();
      return undefined;
    }

    // Schedule local logout for the refresh token expiry so idle sessions do not remain active in the UI.
    const logoutTimer = window.setTimeout(logout, msUntilExpiry);

    return () => window.clearTimeout(logoutTimer);
  }, [token]);

  /**
   * The AuthContext.Provider component makes the authentication data available
   * to all child components.
   */
  return (
    <AuthContext.Provider value={{ token, username, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
